import { c as _c } from "react/compiler-runtime";
import { getHostedBusinessesOptions, postHostedBusinessOnboardingMutation, postHostedBusinessOnboardingOptions } from '@onefootprint/fetch-hosted';
import type { FootprintReasonCode, HostedBusiness, WorkflowFixtureResult } from '@onefootprint/request-types';
import type { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import { CreateBusinessOnboarding } from '@/idv2/features/create-business-onboarding';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { redirectToCheckRequirements } from '@/idv2/utils/loader-navigation';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'create-business-onboarding'
});
const startOnboardingAndRedirect = async (queryClient: QueryClient, token: string, kybFixtureResult?: WorkflowFixtureResult, fixtureRiskSignals?: FootprintReasonCode[]) => {
  const headers = {
    'X-Fp-Authorization': token
  };
  // Create a plain copy to avoid circular reference issues
  const plainFixtureResult = kybFixtureResult ? JSON.parse(JSON.stringify(kybFixtureResult)) : undefined;
  await queryClient.fetchQuery({
    ...postHostedBusinessOnboardingOptions({
      headers,
      body: {
        kybFixtureResult: plainFixtureResult,
        fixtureRiskSignals
      }
    }),
    retry: retryOnConnectionError
  });
  logInfo('Business onboarding created, redirecting to check-requirements');
  return redirectToCheckRequirements(queryClient, token);
};
export const Route = createFileRoute('/onboarding/$token/create-business-onboarding')({
  component: CreateBusinessOnboardingPage,
  loader: async ({
    context: {
      currentRequirement,
      overallOutcome,
      fixtureRiskSignals,
      queryClient
    },
    params: {
      token
    }
  }) => {
    if (currentRequirement?.kind !== 'create_business_onboarding') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    const headers = {
      'X-Fp-Authorization': token
    };
    const {
      requiresBusinessSelection
    } = currentRequirement;
    logInfo('Create business onboarding loader started', {
      requirementKind: currentRequirement.kind,
      requiresBusinessSelection
    });

    // If business selection is not required, start onboarding immediately and redirect
    if (!requiresBusinessSelection) {
      logInfo('No business selection required, starting onboarding automatically');
      await startOnboardingAndRedirect(queryClient, token, overallOutcome, fixtureRiskSignals);
    }

    // Fetch existing businesses for selection
    const businesses: HostedBusiness[] = await queryClient.fetchQuery(getHostedBusinessesOptions({
      headers
    }));
    logInfo('Fetched businesses for selection', {
      count: businesses.length
    });

    // If no existing businesses, start onboarding with a new business and redirect
    if (businesses.length === 0) {
      logInfo('No existing businesses, starting onboarding with new business');
      await startOnboardingAndRedirect(queryClient, token, overallOutcome, fixtureRiskSignals);
    }

    // User needs to select from existing businesses
    return {
      businesses
    };
  }
});
function CreateBusinessOnboardingPage() {
  const $ = _c(16);
  const {
    businesses
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const {
    overallOutcome,
    fixtureRiskSignals
  } = Route.useRouteContext();
  const footprint = useFootprint();
  let t0;
  if ($[0] !== token) {
    t0 = postHostedBusinessOnboardingMutation({
      headers: {
        "X-Fp-Authorization": token
      }
    });
    $[0] = token;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    mutateAsync: startOnboardingMutation,
    isPending
  } = useMutation(t0);
  let t1;
  if ($[2] !== fixtureRiskSignals || $[3] !== footprint.navigation || $[4] !== overallOutcome || $[5] !== startOnboardingMutation) {
    t1 = async inheritBusinessId => {
      logInfo("Starting business onboarding", {
        inheritBusinessId
      });
      await startOnboardingMutation({
        body: {
          inheritBusinessId,
          kybFixtureResult: overallOutcome,
          fixtureRiskSignals
        }
      });
      logInfo("Create business onboarding completed successfully");
      footprint.navigation.forward();
    };
    $[2] = fixtureRiskSignals;
    $[3] = footprint.navigation;
    $[4] = overallOutcome;
    $[5] = startOnboardingMutation;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const startOnboarding = t1;
  let t2;
  if ($[7] !== startOnboarding) {
    t2 = () => startOnboarding();
    $[7] = startOnboarding;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleAddNew = t2;
  let t3;
  if ($[9] !== startOnboarding) {
    t3 = businessId => startOnboarding(businessId);
    $[9] = startOnboarding;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleSelect = t3;
  let t4;
  if ($[11] !== businesses || $[12] !== handleAddNew || $[13] !== handleSelect || $[14] !== isPending) {
    t4 = <CreateBusinessOnboarding businesses={businesses} onAddNew={handleAddNew} onSelect={handleSelect} isBusy={isPending} />;
    $[11] = businesses;
    $[12] = handleAddNew;
    $[13] = handleSelect;
    $[14] = isPending;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}